import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, Subject, of } from 'rxjs';
import { filter, finalize, map, shareReplay, switchMap, take, tap, takeUntil, withLatestFrom } from 'rxjs/operators';

import { toSignal } from '@angular/core/rxjs-interop';
import { CombinedClientHttpService } from '@sondermind/data-access/clinical';
import { FileUploadEventService, PatientDocumentsStore, SharedFileStore } from '@sondermind/data-access/documents';
import {
  FEATURE_FLAG_SERVICE,
  FeatureFlagKey,
  IFeatureFlagService,
  IFeatureFlagValueTypes
} from '@sondermind/data-access/feature-flags';
import { PaginationComponent } from '@sondermind/features/pagination';
import { ClientDetailPageService } from '@sondermind/features/provider-facing/client-detail';
import { DocSendModalService } from '@sondermind/features/provider-facing/therapist-documents';
import { IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisCardModule } from '@sondermind/iris/card';
import { IconColors, IconSizes, IconUsageTypes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { IrisButtonTarget } from '@sondermind/iris/shared';
import { IrisSpinnerComponent } from '@sondermind/iris/spinner';
import { IrisTooltipComponent } from '@sondermind/iris/tooltip';
import { DocumentStatus, IPatientDocument, ISharedFile } from '@sondermind/utilities/models-documents';
import { Patient } from '@sondermind/utilities/models-patient';
import { LocalizedDatePipe } from '@sondermind/utilities/time-and-date';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';
import { Logger } from '@sondermind/datadog-logging';
import { IrisModalService, IrisModalSize } from '@sondermind/iris/modal';
import { ConfirmationModalComponent } from '@sondermind/features/modals/confirmation';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';
import { RFS_HTTP, IRfsHttpPort } from '../../modules/rfs/rfs-review-page/models/rfs-http.token';
import {
  IRfsSubmissionListItem,
  RFS_STATUS,
  RfsStatus
} from '../../modules/rfs/rfs-review-page/models/rfs-prefill.model';
import { RFS_COPY, RFS_STATUS_LABELS, formatRfsDateShort } from '../../modules/rfs/rfs-review-page/rfs.constants';

/** A VA RFS submission row shaped for the template (label + Expired flag + row action). */
interface IRfsSubmissionRow extends IRfsSubmissionListItem {
  statusLabel: string;
  isExpired: boolean;
  /** "For auth #{authorizationNumber}(Exp MM/DD/YY)" subline (Figma Frame 1618867690). */
  authSubline: string;
  /** Draft rows offer a "Resume" button that reopens the form (Figma Frame 1618867690). */
  canResume: boolean;
  /** Failed rows offer a "Try again" button that reopens the form for a fresh submission. */
  canRetry: boolean;
  /** Terminal rows with a generated PDF offer a per-row download. */
  canDownload: boolean;
  /** Only "Submitted to VA" rows carry a status tooltip. */
  hasStatusTooltip: boolean;
}

@Component({
  selector: 'client-detail-documents',
  templateUrl: './client-detail-documents.component.html',
  styleUrls: ['./client-detail-documents.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IrisButtonModule,
    IrisCardModule,
    IrisIconComponent,
    IrisIconButtonModule,
    IrisSpinnerComponent,
    IrisTooltipComponent,
    LocalizedDatePipe,
    PaginationComponent
  ]
})
export class ClientDetailDocumentsPage implements OnInit, OnDestroy {
  readonly loading = signal<boolean>(true);
  private destroyed$ = new Subject<void>();
  private currentClientId: number | null = null;

  private patient$: Observable<Patient> = this.clientDetailPageService.patient.pipe(
    tap(() => this.loading.set(false)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private docs$: Observable<IPatientDocument[]> = this.clientDetailPageService.getPatientId$().pipe(
    switchMap((id) => this.patientDocumentsStore.getPatientDocuments$(id))
  );

  readonly patient = toSignal(this.patient$);
  readonly docs = toSignal(this.docs$);
  readonly hasClientRecord = computed(() => {
    const patient = this.patient();
    return patient ? patient.haveClientRecord && !patient.accountPending : false;
  });

  displayedColumns: string[] = ['title', 'sentDate', 'status', 'download'];

  readonly documentStatus = DocumentStatus;
  readonly irisButtonTarget = IrisButtonTarget;
  readonly irisIconButtonTypes = IrisIconButtonTypes;
  /** Resume action on draft RFS rows renders as a primary Iris button (Figma). */
  readonly rfsResumeButtonType = IrisButtonTypes.PRIMARY;
  readonly iconColors = IconColors;
  readonly iconSizes = IconSizes;
  readonly iconUsageTypes = IconUsageTypes;
  readonly docDisclosureInfo = InfoLinks.DOC_DISCLOSURES;
  readonly isAutomatedConsentDocsEnabled = signal<boolean>(false);
  readonly displayFileUploadHistory = signal<boolean>(false);
  readonly isSaasEnabled = signal<boolean>(false);

  readonly sendDocsText = computed(() => {
    if (this.isSaasEnabled()) {
      return 'Send documents to your client via SonderMind chat messages by clicking the Message button above ' +
        'and adding them as a file attachment.';
    }
    if (this.isAutomatedConsentDocsEnabled()) {
      return 'SonderMind automatically sends the initial documents, and you can send them manually here too.';
    }
    return 'Send documents for your client to complete through their portal—they\'ll be notified via email.';
  });

  readonly noAccountInfoText = computed(() => {
    if (this.isAutomatedConsentDocsEnabled()) {
      return 'Consent documents will be sent automatically once the client finishes creating their account.';
    }
    return 'You\'ll be able to send documents once this client has finished creating their account.';
  });

  /** Shared files pagination state from the store. */
  readonly sharedFilesState = this.sharedFileStore.state;

  /** UUID of the file currently being toggled (for loading state). */
  readonly togglingFileUuid = signal<string | null>(null);

  constructor(
    private appService: SondermindApplicationService,
    private clientDetailPageService: ClientDetailPageService,
    private patientDocumentsStore: PatientDocumentsStore,
    private docSendModalService: DocSendModalService,
    private combinedClientHttpService: CombinedClientHttpService,
    private sharedFileStore: SharedFileStore,
    private toastService: NotificationToastService,
    private logger: Logger,
    private irisModalService: IrisModalService,
    private fileUploadEventService: FileUploadEventService,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
    @Inject(RFS_HTTP)
    private rfsHttp: IRfsHttpPort,
    private router: Router,
  ) { }

  /** VA RFS in-app feature (VA_RFS_ENABLED flag) — gates the RFS submissions list on this page. */
  readonly vaRfsEnabled = signal<boolean>(false);

  /** The client id for the RFS submissions list (persona_client_id). */
  readonly personaClientId = computed(() => this.patient()?.personaClientId ?? null);

  /** VA RFS submissions list state (behind VA_RFS_ENABLED) — the "coverage & authorizations" card. */
  readonly rfsCopy = RFS_COPY;
  readonly rfsLoading = signal<boolean>(true);
  readonly rfsLoadError = signal<boolean>(false);
  private readonly rfsSubmissions = signal<IRfsSubmissionListItem[]>([]);
  /**
   * Id of the VA-form row whose PDF download is currently in flight, or null. Drives a
   * per-row loading spinner + disables that row's download button so a provider can't
   * fire multiple downloads with rapid clicks (the streamed PDF can take a moment).
   */
  private readonly downloadingRfsId = signal<number | string | null>(null);

  /** RFS rows with the display label + Expired flag + row action derived for the template. */
  readonly rfsRows = computed<IRfsSubmissionRow[]>(() => this.rfsSubmissions().map((submission) => {
    const isExpired = this.isRfsExpired(submission);
    return {
      ...submission,
      statusLabel: isExpired ? RFS_STATUS_LABELS.expired : RFS_STATUS_LABELS[submission.status],
      isExpired,
      authSubline: this.rfsAuthSubline(submission),
      canResume: submission.status === RFS_STATUS.DRAFT && !isExpired,
      canRetry: submission.status === RFS_STATUS.FAILED && !isExpired,
      canDownload: this.isTerminalRfs(submission.status) && submission.pdfAvailable,
      hasStatusTooltip: submission.status === RFS_STATUS.SUBMITTED_TO_VA
    };
  }));

  ngOnInit(): void {
    // SAAS providers are not able to manually send documents to clients via this page,
    // and can only send documents via chat.
    this.isSaasEnabled.set(this.appService.isSaasProvider());

    // Check automated consent docs feature flag
    this.featureFlagService.isEnabled$(FeatureFlagKey.AUTOMATED_CONSENT_DOCS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isEnabled) => {
        this.isAutomatedConsentDocsEnabled.set(isEnabled);
      });

    // Check display-file-upload-history feature flag and load shared files
    this.featureFlagService.evaluate$<IFeatureFlagValueTypes[FeatureFlagKey.DISPLAY_FILE_UPLOAD_HISTORY]>(
      FeatureFlagKey.DISPLAY_FILE_UPLOAD_HISTORY,
      { displayInClientPortal: false, displayInProviderPortal: false }
    )
      .pipe(
        takeUntil(this.destroyed$),
        tap((variant) => {
          this.displayFileUploadHistory.set(variant?.payload?.displayInProviderPortal ?? false);
        }),
        filter((variant) => variant?.payload?.displayInProviderPortal ?? false),
        switchMap(() => this.clientDetailPageService.patient)
      )
      .subscribe((patient) => {
        this.currentClientId = patient.personaClientId;
        // Always refresh when patient is loaded (handles tab switching and initial load)
        // This ensures fresh data even if files were uploaded while on a different tab
        this.sharedFileStore.invalidateCache();
        this.sharedFileStore.goToPage(patient.personaClientId, 1);
      });

    // Subscribe to file upload events for real-time updates while viewing the tab
    // Use withLatestFrom to ensure currentClientId is available before filtering
    this.fileUploadEventService.fileUploaded$.pipe(
      takeUntil(this.destroyed$),
      withLatestFrom(
        this.clientDetailPageService.patient.pipe(
          map((patient) => patient?.personaClientId),
          filter((id): id is number => id !== null && id !== undefined)
        )
      ),
      filter(([event, personaClientId]) => event.personaClientId === personaClientId)
    ).subscribe(([event]) => {
      // Refresh the current page to show newly uploaded files in real-time
      if (this.currentClientId) {
        this.sharedFileStore.invalidateCache();
        this.sharedFileStore.goToPage(
          this.currentClientId,
          this.sharedFilesState().currentPage
        );
      }
    });

    // VA RFS submissions list (behind the VA_RFS_ENABLED flag): when enabled, load the
    // client's submissions once the patient's persona_client_id is available.
    this.featureFlagService.isEnabled$(FeatureFlagKey.VA_RFS_ENABLED)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isEnabled) => {
        this.vaRfsEnabled.set(isEnabled);
        if (isEnabled) {
          this.loadRfsSubmissions();
        }
      });

    this.loading.set(false);
  }

  /**
   * Loads the VA RFS submissions for this client once the patient (persona_client_id)
   * is available, shaping them into the "coverage & authorizations" list.
   */
  private loadRfsSubmissions(): void {
    this.patient$.pipe(
      filter((patient): patient is Patient => !!patient),
      take(1),
      switchMap(({ personaClientId }) => {
        // A client without a persona_client_id has no RFS submissions to fetch (account
        // not yet provisioned). Resolve to an empty list rather than leaving the stream
        // un-emitting — otherwise the loading spinner would hang forever.
        if (personaClientId === null || personaClientId === undefined) {
          return of<IRfsSubmissionListItem[]>([]);
        }
        return this.rfsHttp.listSubmissionsForClient(personaClientId.toString());
      }),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (items) => {
        this.rfsSubmissions.set(items ?? []);
        this.rfsLoading.set(false);
      },
      error: (err: unknown) => {
        this.logger.error('ClientDetailDocumentsPage: failed to load RFS submissions', { err });
        this.rfsLoadError.set(true);
        this.rfsLoading.set(false);
      }
    });
  }

  /**
   * Expired = the backend marked the row `expired`, OR (until the backend emits that status)
   * a non-terminal-success row whose auth is past its expiration. Terminal-success rows
   * (completed / submitted_to_va) are never expired.
   */
  private isRfsExpired(submission: IRfsSubmissionListItem): boolean {
    if (this.isTerminalRfs(submission.status)) {
      return false;
    }
    if (submission.status === RFS_STATUS.EXPIRED) {
      return true;
    }
    if (!submission.expiresAt) {
      return false;
    }
    return new Date(submission.expiresAt).getTime() < Date.now();
  }

  /** Terminal-success statuses: signed & submitted (drives Expired suppression + download). */
  private isTerminalRfs(status: RfsStatus): boolean {
    return status === RFS_STATUS.COMPLETED || status === RFS_STATUS.SUBMITTED_TO_VA;
  }

  /**
   * "For auth #{authorizationNumber}(Exp MM/DD/YY)" — the human auth NUMBER + expiration
   * date (Figma Frame 1618867690). Falls back to the internal auth id if the number is
   * absent, and omits the "(Exp …)" suffix when there's no expiration date.
   */
  private rfsAuthSubline(submission: IRfsSubmissionListItem): string {
    const authRef = submission.authorizationNumber ?? submission.referralAuthorizationId ?? '';
    const expDate = formatRfsDateShort(submission.expiresAt);
    const exp = expDate ? `(Exp ${expDate})` : '';
    return `For auth #${authRef}${exp}`;
  }

  /**
   * Resume a draft RFS: reopens the review page for that submission's authorization
   * (Figma Frame 1618867690 — the "Resume" action on draft rows).
   */
  onResumeRfs(row: IRfsSubmissionRow): void {
    this.openRfsReviewPage(row);
  }

  /**
   * Retry a failed RFS: reopens the review page for that authorization. The page loads a
   * fresh prefill and a re-confirm creates a new submission (the failed row remains as
   * history), so this recovers from a failed submit without any special back-end call.
   */
  onRetryRfs(row: IRfsSubmissionRow): void {
    this.openRfsReviewPage(row);
  }

  /** Navigates to the RFS review page for a row's authorization (Resume + Try again). */
  private openRfsReviewPage(row: IRfsSubmissionRow): void {
    if (row.referralAuthorizationId == null) {
      return;
    }
    this.router.navigate(['/account', 'rfs', row.referralAuthorizationId, 'review']);
  }

  /** True while THIS row's VA-form PDF download is in flight (drives the row spinner). */
  isDownloadingRfs(row: IRfsSubmissionRow): boolean {
    return row.referralAuthorizationId != null && this.downloadingRfsId() === row.referralAuthorizationId;
  }

  /**
   * Downloads a terminal submission's generated PDF. The endpoint is auth-gated and
   * streams the bytes same-origin, so we fetch it as a blob (via the RFS port) and open
   * an object URL rather than linking directly (a bare link carries no bearer token).
   *
   * Guards against multiple clicks: while a row's download is in flight the row id is
   * tracked (isDownloadingRfs → spinner + disabled button), and a repeat click is a no-op
   * until the download settles.
   */
  onDownloadRfs(row: IRfsSubmissionRow): void {
    if (row.referralAuthorizationId == null || this.isDownloadingRfs(row)) {
      return;
    }
    this.downloadingRfsId.set(row.referralAuthorizationId);
    this.rfsHttp.downloadPdf(row.referralAuthorizationId.toString())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (blob) => {
          this.downloadingRfsId.set(null);
          if (blob) {
            this.triggerBrowserDownload(blob, `${this.rfsCopy.listDocumentName}.pdf`);
          }
        },
        error: (error: unknown) => {
          this.downloadingRfsId.set(null);
          this.toastService.showNotificationToast({
            type: ToastType.ERROR,
            message: this.rfsCopy.pdfDownloadError,
            duration: 5000
          });
          this.logger.error('Error downloading RFS PDF', { error, authId: row.referralAuthorizationId });
        }
      });
  }

  reloadData(): void {
    this.loading.set(true);

    /* HACK: Request patient documents from the store, causing it to refetch the patient documents
    and emit a new value through the `docs$` observable. The document store should probably be refactored
    to contain a sendDocuments method itself and update the patient documents collection internally.
    Whenever that happens the entire reloadData pattern should no longer be needed. */
    this.patient$.pipe(
      switchMap(
        (patient) => this.patientDocumentsStore.getPatientDocuments$(patient.id)
      ),
      finalize(() => this.loading.set(false))
    ).subscribe();
  }

  sendDocs(): void {
    this.patient$.pipe(
      switchMap((patient) => this.docSendModalService.openDocSendModal$(
        patient,
        null,
        this.getClientReferralSource$(patient.platformIdentity),
        false
      ))
    ).subscribe((closed) => {
      if (closed) {
        this.reloadData();
      }
    });
  }

  onSharedFilesPageChange(page: number): void {
    if (this.currentClientId) {
      this.sharedFileStore.goToPage(this.currentClientId, page);
    }
  }

  /**
   * Download a shared file.
   * @param file - The shared file to download.
   */
  downloadSharedFile(file: ISharedFile): void {
    if (!file.uuid) {
      return;
    }

    this.sharedFileStore.downloadSharedFile$(file.uuid).pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (blob) => {
        if (blob) {
          this.triggerBrowserDownload(blob, this.decodeFileName(file.name));
        }
      },
      error: (error: unknown) => {
        this.toastService.showNotificationToast({
          type: ToastType.ERROR,
          message: 'Error downloading file',
          duration: 5000
        });
        this.logger.error('Error downloading shared file', {
          error,
          fileUuid: file.uuid,
          fileName: file.name
        });
      }
    });
  }

  /**
   * Trigger browser download for a blob.
   * @param blob - The blob to download.
   * @param filename - The filename for the download.
   */
  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename || 'download';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Slight delay before revoking for better Safari compatibility
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  /**
   * Decode a filename if it's URL encoded.
   * @param filename - The filename to decode.
   * @returns The decoded filename.
   */
  private decodeFileName(filename: string): string {
    try {
      return decodeURIComponent(filename);
    } catch (error) {
      // If decoding fails, return the original filename
      return filename;
    }
  }

  /**
   * Open confirmation modal for hiding/unhiding a file.
   * @param file - The shared file to toggle visibility for.
   */
  openHideConfirmationModal(file: ISharedFile): void {
    const action = file.hidden ? 'unhide' : 'hide';
    const titleText = `Are you sure you would like to ${action} this file?`;
    const bodyText = file.hidden
      ? 'This file will become visible and downloadable again.'
      : 'This file will be hidden and no longer available for download.';
    const confirmBtnText = file.hidden ? 'Unhide' : 'Hide';

    const modalRef = this.irisModalService.open<ConfirmationModalComponent, any, boolean>(
      ConfirmationModalComponent,
      {
        data: {
          titleText,
          bodyText,
          confirmBtnText,
          isOpened: true
        },
        size: IrisModalSize.MD
      }
    );

    modalRef.afterClosed$().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.toggleFileVisibility(file);
      }
    });
  }

  /**
   * Toggle file visibility (hide/unhide).
   * @param file - The shared file to toggle visibility for.
   */
  toggleFileVisibility(file: ISharedFile): void {
    this.togglingFileUuid.set(file.uuid);

    this.sharedFileStore.updateFileVisibility$(file.uuid, !file.hidden).pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: () => {
        // Store handles optimistic update, component refetches to ensure consistency
        if (this.currentClientId) {
          this.sharedFileStore.goToPage(this.currentClientId, this.sharedFilesState().currentPage);
        }
        this.togglingFileUuid.set(null);
      },
      error: (error: unknown) => {
        this.toastService.showNotificationToast({
          type: ToastType.ERROR,
          message: 'Failed to update file visibility',
          duration: 5000
        });
        this.logger.error('Error updating file visibility', { error, fileUuid: file.uuid });
        this.togglingFileUuid.set(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private getClientReferralSource$(platformIdentity: string): Observable<string | null> {
    return this.combinedClientHttpService
      .getCombinedClientByPlatformIdentity(platformIdentity).pipe(
        map((combinedClient) => combinedClient.referralSource)
      );
  }
}
